import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin, Printer, Layers } from "lucide-react";
import { IMAGES } from "../data/siteData";
import { EASE } from "./Reveal";

const TITLE_LINES = [
    <>Dove le idee</>,
    <>prendono <span className="font-serif-accent italic font-medium text-[#FF0066]">forma</span></>,
    <>e materia.</>,
];

const BADGES = [
    { icon: MapPin, label: "Gela, Sicilia", testid: "hero-badge-location" },
    { icon: Printer, label: "Laboratorio artigianale", testid: "hero-badge-lab" },
    { icon: Layers, label: "Progettazione + Produzione + Posa", testid: "hero-badge-process" },
];

export const Hero = () => {
    const frameRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: frameRef, offset: ["start end", "end start"] });
    const parallaxY = useTransform(scrollYProgress, [0, 1], [-46, 46]);

    return (
        <section id="home" data-testid="hero-section" className="relative pt-32 sm:pt-40 pb-16 sm:pb-24 overflow-hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                    <div className="lg:col-span-7">
                        <motion.p
                            data-testid="hero-eyebrow"
                            className="font-mono-label text-xs text-[#FF0066] mb-6"
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, ease: EASE }}
                        >
                            Laboratorio di progettazione grafica &amp; stampa a Gela
                        </motion.p>
                        <h1 data-testid="hero-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-[#0E0E10]">
                            {TITLE_LINES.map((line, i) => (
                                <span key={i} className="block overflow-hidden pb-1 -mb-1">
                                    <motion.span
                                        className="block"
                                        initial={{ y: "115%" }}
                                        animate={{ y: 0 }}
                                        transition={{ duration: 0.95, delay: 0.12 + i * 0.14, ease: EASE }}
                                    >
                                        {line}
                                    </motion.span>
                                </span>
                            ))}
                        </h1>
                        <motion.p
                            data-testid="hero-subtitle"
                            className="mt-6 text-base sm:text-lg leading-relaxed text-zinc-600 max-w-xl"
                            initial={{ opacity: 0, y: 22 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.62, ease: EASE }}
                        >
                            Dal progetto digitale all'allestimento fisico. Trasformiamo visioni in grafiche
                            d'impatto, carte da parati sartoriali, tipografia d'eccellenza e allestimenti
                            per spazi commerciali.
                        </motion.p>
                        <motion.div
                            className="mt-10 flex flex-wrap items-center gap-4"
                            initial={{ opacity: 0, y: 22 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.76, ease: EASE }}
                        >
                            <a
                                href="#servizi"
                                data-testid="hero-cta-primary"
                                className="group inline-flex items-center gap-2 bg-[#0E0E10] text-white font-semibold text-sm px-7 py-4 hover:bg-[#FF0066] transition-colors duration-300"
                            >
                                Esplora i Servizi
                                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                            </a>
                            <a
                                href="#contatti"
                                data-testid="hero-cta-secondary"
                                className="inline-flex items-center gap-2 border border-[#0E0E10] text-[#0E0E10] font-semibold text-sm px-7 py-4 hover:bg-[#0E0E10] hover:text-white transition-colors duration-300"
                            >
                                Contattaci
                            </a>
                        </motion.div>
                        <div className="mt-12 flex flex-wrap gap-3" data-testid="hero-badges">
                            {BADGES.map((badge, i) => (
                                <motion.span
                                    key={badge.testid}
                                    data-testid={badge.testid}
                                    className="inline-flex items-center gap-2 text-xs font-mono-label text-zinc-500 border border-zinc-300 px-4 py-2"
                                    initial={{ opacity: 0, y: 16 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.7, delay: 0.92 + i * 0.1, ease: EASE }}
                                >
                                    <badge.icon size={13} className="text-[#FF0066]" /> {badge.label}
                                </motion.span>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-5">
                        <motion.div
                            className="relative"
                            data-testid="hero-image-frame"
                            ref={frameRef}
                            initial={{ opacity: 0, scale: 0.96, y: 26 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            transition={{ duration: 1, delay: 0.5, ease: EASE }}
                        >
                            <div className="registration-mark -top-3 -left-3" />
                            <div className="registration-mark -top-3 -right-3" />
                            <div className="registration-mark -bottom-3 -left-3" />
                            <div className="registration-mark -bottom-3 -right-3" />
                            <div className="border border-[#0E0E10] bg-white p-3 shadow-[0_20px_50px_rgba(0,0,0,0.12)]">
                                <div className="overflow-hidden">
                                    <motion.img
                                        src={IMAGES.hero}
                                        alt="Murale artistico realizzato da Studio Arti Grafiche"
                                        data-testid="hero-image"
                                        style={{ y: parallaxY }}
                                        className="w-full aspect-[4/5] object-cover scale-[1.14]"
                                    />
                                </div>
                                <div className="cmyk-strip mt-3">
                                    <span className="c" /><span className="m" /><span className="y" /><span className="k" />
                                </div>
                            </div>
                            <p className="mt-4 text-xs text-zinc-500 italic text-right">
                                Colori, carte e strumenti del mestiere — il nostro laboratorio
                            </p>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
};
